import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Col, Row, Typography } from 'antd';
import dayjs from 'dayjs';

import { AppDispatch } from 'app/store';

import Alert from 'components/Alert/Alert';
import DataAccordionView from 'components/DataAccordionView/DataAccordionView';
import JobSubscriptionStatus from 'components/JobSubscriptionStatus/JobSubscriptionStatus';
import { getJobActionsPermissions } from 'components/JobsTable/utils/getJobActionsPermissions';
import Tags from 'components/Jobseekers/components/Tags';
import LocationDisplay from 'components/LocationDisplay/LocationDisplay';
import CardActionsMenu from 'components/SelectCardWrapper/CardActionsMenu';
import SidebarNavigation from 'components/SidebarNavigation';
import StageSelector from 'components/StageSelector/StageSelector';
import * as jobStatusConfig from 'components/StageSelector/jobStatusConfig';
import StatusIcon from 'components/StatusIcon/StatusIcon';

import { selectClientCardLastFourDigits } from 'features/client/clientSelectors';
import ClientsService from 'features/client/clientService';
import { selectDictionary } from 'features/dictionaries/dictionariesSelectors';
import { selectJobs } from 'features/jobs/jobsSelectors';
import jobsService from 'features/jobs/jobsService';
import { upsertJob } from 'features/jobs/jobsSlice';
import { fetchJobsData } from 'features/jobs/jobsThunks';
import {
  selectActiveUserId,
  selectIsBlockSidebarUrlHandling
} from 'features/sidebar/sidebarSelectors';
import { closeSidebar, setActiveUserId } from 'features/sidebar/sidebarSlice';

import useErrorCatch from 'hooks/useErrorCatch';
import usePageviewEvent from 'hooks/usePageViewEvent';
import { useSyncJobRoute } from 'hooks/useSyncJobRoute';

import formatAndOrderData from 'utils/formatAndOrderData';
import { isNil } from 'utils/isNil';
import { getDisplayJobTitle } from 'utils/jobUtils';
import { notify } from 'utils/toast/createToast';

import { FormattedOption } from 'types/dictionaries';
import { Job } from 'types/jobs';

import {
  AlertType,
  Dictionaries,
  GTMEvents,
  PaymentStatus,
  SidebarContentTypes,
  SubscriptionTypes
} from 'constants/enums';
import languagesOrder from 'constants/languagesOrder';
import {
  DATE_FORMAT,
  DEBOUNCE_JOBS_TIMEOUT_MS,
  FALLBACK_VALUE
} from 'constants/uiSettings';

import styles from './ClientJobView.module.scss';
import Salary from './Salary/Salary';

const ClientJobView = () => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();

  const catchError = useErrorCatch();
  const jobId = useSelector(selectActiveUserId);
  const jobs = useSelector(selectJobs);
  const skillsetDict = useSelector(
    selectDictionary(Dictionaries.Skillset)
  ) as FormattedOption[];
  const jobStatuses = useSelector(selectDictionary(Dictionaries.JobStatus));
  const languagesDict = useSelector(selectDictionary(Dictionaries.Language));
  const clientCardLastFourDigits = useSelector(selectClientCardLastFourDigits);
  const languagesOptions = formatAndOrderData(languagesDict, languagesOrder);
  const isBlockUrlHandling = useSelector(selectIsBlockSidebarUrlHandling);
  useSyncJobRoute(jobId, isBlockUrlHandling);

  const job = jobs.find((job: Job) => job.id === jobId) || null;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isPaymentLoading, setIsPaymentLoading] = useState<boolean>(false);

  usePageviewEvent(GTMEvents.JobListViewSidebar);

  const fetchJobs = useCallback(async () => {
    dispatch(fetchJobsData());
  }, [dispatch]);

  const fetchJobData = useCallback(
    async (jobId: string): Promise<void> => {
      try {
        setIsLoading(true);
        const result = await jobsService.getJobById(jobId);
        dispatch(upsertJob(result));
      } catch (error) {
        catchError({
          error,
          default: {
            message: t('error.message.operationFailed'),
            description: t('error.message.failedFetchJob')
          }
        });
        dispatch(closeSidebar());
      } finally {
        setIsLoading(false);
      }
    },
    [setIsLoading, dispatch]
  );

  const debouncedFetchJobs = useCallback(() => {
    setTimeout(() => {
      fetchJobs();
    }, DEBOUNCE_JOBS_TIMEOUT_MS);
  }, [fetchJobs]);

  const debouncedFetchJobData = useCallback(() => {
    setTimeout(() => {
      fetchJobData(jobId);
    }, DEBOUNCE_JOBS_TIMEOUT_MS);
  }, [fetchJobData, jobId]);

  useEffect(() => {
    if (!jobId) {
      return;
    }
    fetchJobData(jobId);
  }, [fetchJobData, jobId]);

  const isFreeJob = job?.subscriptionType === SubscriptionTypes.Free;
  const jobActionsPermissions = getJobActionsPermissions(t, job);
  const { alertType, alertText } = useMemo(() => {
    let alertType = AlertType.Error;
    let alertText = '';

    if (isFreeJob) {
      return { alertType, alertText };
    }

    switch (job?.subscriptionStatus) {
      case PaymentStatus.Active: {
        return { alertType, alertText };
      }

      case PaymentStatus.Canceled: {
        alertText = t('sidebar.job.alert.paymentCanceled');
        alertType = AlertType.Info;
        break;
      }

      case PaymentStatus.IncompleteExpired: {
        alertText = t('sidebar.job.alert.paymentIncomplete');
        break;
      }

      case PaymentStatus.PastDue: {
        alertText = t('sidebar.job.alert.paymentPastDue');
        alertType = AlertType.Warning;
        break;
      }
    }

    return { alertText, alertType };
  }, [job, isFreeJob]);

  const isShowReactivateJobButton =
    job?.subscriptionStatus === PaymentStatus.Canceled;

  const isShowRetryPaymentButton = [
    PaymentStatus.Unpaid,
    PaymentStatus.Incomplete,
    PaymentStatus.PastDue,
    PaymentStatus.Expired
  ].some((status) => job?.subscriptionStatus === status);

  const handleReactivate = () => {
    setIsPaymentLoading(true);
    ClientsService.subscribePaidJob(jobId)
      .then(() => {
        notify({
          type: AlertType.Success,
          message: t('sidebar.job.notify.jobActive')
        });
        debouncedFetchJobs();
        debouncedFetchJobData();
      })
      .finally(() => {
        setIsPaymentLoading(false);
      });
  };

  const handleRetryPayment = () => {
    setIsPaymentLoading(true);
    ClientsService.retryPaymentForPaidJob(jobId)
      .then(() => {
        notify({
          type: AlertType.Success,
          message: t('sidebar.job.notify.paymentWentThrough')
        });
        debouncedFetchJobs();
        debouncedFetchJobData();
      })
      .catch((error) => {
        catchError({
          error,
          default: {
            description: t('error.message.cantProcessPayment')
          }
        });
      })
      .finally(() => {
        setIsPaymentLoading(false);
      });
  };

  return (
    <aside className={styles.jobDetailsContainer}>
      <SidebarNavigation id={jobId} data={jobs} action={setActiveUserId} />

      {isLoading || !job ? (
        <div>{t('common.loading')}</div>
      ) : (
        <>
          <div className={styles.header}>
            {job && (
              <div className={styles.title}>
                {getDisplayJobTitle(job) || t('job.defaultTitle')}
              </div>
            )}
            <CardActionsMenu
              sidebarContentType={SidebarContentTypes.JobView}
              permissions={jobActionsPermissions}
              contentId={job?.id}
            />
          </div>

          {alertText && (
            <Row className={styles.row}>
              <Col span={24}>
                <Alert type={alertType} message={alertText} />
              </Col>
            </Row>
          )}

          <Row className={styles.row}>
            <Col span={12}>
              <StageSelector
                value={job.status}
                hasPermission
                isDisplayIcons
                id={jobId}
                options={jobStatuses}
                config={jobStatusConfig}
                dictionary={Dictionaries.JobClientStatusFlow}
              />
              <div className={styles.label}>{t('sidebar.job.status')}</div>
            </Col>

            <Col span={12}>
              <JobSubscriptionStatus job={job} />
            </Col>
          </Row>

          {!isFreeJob && (
            <>
              <Row className={styles.row}>
                <div className={styles.subheader}>
                  {t('sidebar.job.subscriptionSummary')}
                </div>
                <div className={styles.divider} />
              </Row>

              {isShowReactivateJobButton && (
                <Row className={styles.row}>
                  <Button
                    type="primary"
                    className={styles.button}
                    onClick={handleReactivate}
                    loading={isPaymentLoading}
                  >
                    {t('sidebar.job.reactivate')}
                  </Button>
                </Row>
              )}

              {isShowRetryPaymentButton && (
                <Row className={styles.row}>
                  <Button
                    type="primary"
                    className={styles.button}
                    onClick={handleRetryPayment}
                    loading={isPaymentLoading}
                  >
                    {t('sidebar.job.retryPayment')}
                  </Button>
                </Row>
              )}

              <Row className={styles.row}>
                <Col span={12}>
                  {
                    <div>
                      {clientCardLastFourDigits
                        ? `**** **** **** ${clientCardLastFourDigits}`
                        : FALLBACK_VALUE}
                    </div>
                  }
                  <div className={styles.label}>
                    {t('sidebar.job.paymentMethod')}
                  </div>
                </Col>
              </Row>

              <Row className={styles.row}>
                <Col span={12}>
                  {/* createdDate used as  billing start date*/}
                  <div>
                    {job?.createdDate
                      ? dayjs(job.createdDate).format(DATE_FORMAT)
                      : FALLBACK_VALUE}
                  </div>
                  <div className={styles.label}>
                    {t('sidebar.job.billingStartDate')}
                  </div>
                </Col>

                <Col span={12}>
                  <div>
                    {job.subscriptionDueDate !== null
                      ? dayjs(job.subscriptionDueDate).format(DATE_FORMAT)
                      : FALLBACK_VALUE}
                  </div>
                  <div className={styles.label}>
                    {t('sidebar.job.nextRenewal')}
                  </div>
                </Col>
              </Row>
            </>
          )}

          <Row className={styles.row}>
            <div className={styles.subheader}>{t('sidebar.job.details')}</div>
            <div className={styles.divider} />
          </Row>

          <Row className={styles.row}>
            <Col span={24}>
              <LocationDisplay
                location={job?.location}
                countryData={job?.country}
                address={job?.address}
              />
              <div className={styles.label}>{t('sidebar.job.jobAddress')}</div>
            </Col>
          </Row>

          {job?.description && (
            <DataAccordionView
              title={t('sidebar.job.jobDescription')}
              placeholder={FALLBACK_VALUE}
            >
              <Row className={styles.row}>
                <Col span={24}>
                  <Typography.Paragraph className={styles.jobDescription}>
                    {job.description}
                  </Typography.Paragraph>
                </Col>
              </Row>
            </DataAccordionView>
          )}

          <Row className={styles.row}>
            <Col span={12}>
              <div className={styles.cut}>
                {job?.workSchedule || FALLBACK_VALUE}
              </div>
              <div className={styles.label}>
                {t('sidebar.job.workSchedule')}
              </div>
            </Col>

            <Col span={12}>
              <div>{job?.startDate || FALLBACK_VALUE}</div>
              <div className={styles.label}>
                {t('sidebar.job.estimatedStartDate')}
              </div>
            </Col>
          </Row>

          <Row className={styles.row}>
            <Col span={12}>
              <Salary
                actualSalary={job?.actualSalary}
                payrollRates={job?.payrollRates}
              />
              <div className={styles.label}>{t('sidebar.job.salary')}</div>
            </Col>

            <Col span={12}>
              {!isNil(job?.numberOfInterviews)
                ? job.numberOfInterviews
                : FALLBACK_VALUE}
              <div className={styles.label}>
                {t('sidebar.job.numberOfInterviews')}
              </div>
            </Col>
          </Row>

          <Row className={styles.row}>
            <Col span={24}>
              <Tags dictionary={languagesOptions} values={job?.language} />
              <div className={styles.label}>
                {t('sidebar.job.languageRequirement')}
              </div>
            </Col>
          </Row>

          <Row className={styles.row}>
            <Col span={24}>
              <Tags dictionary={skillsetDict} values={job?.skillRequirement} />
              <div className={styles.label}>
                {t('sidebar.job.skillsetsRequired')}
              </div>
            </Col>
          </Row>

          <Row className={styles.row}>
            <Col span={24}>
              <div>{job.qualifications || FALLBACK_VALUE}</div>
              <div className={styles.label}>
                {t('sidebar.job.certifications')}
              </div>
            </Col>
          </Row>

          <Row className={styles.row}>
            {job?.backCheck && (
              <Col span={12} className={styles.status}>
                <StatusIcon status />
                <div className={styles.label}>{t('sidebar.job.benefits')}</div>
              </Col>
            )}

            {job?.backCheck && (
              <Col span={12} className={styles.status}>
                <StatusIcon status />
                <div className={styles.label}>
                  {t('sidebar.job.backgroundCheckConsent')}
                </div>
              </Col>
            )}

            {job?.drugTest && (
              <Col span={12} className={styles.status}>
                <StatusIcon status />
                <div className={styles.label}>{t('sidebar.job.drugTest')}</div>
              </Col>
            )}
          </Row>

          <Row className={styles.row}>
            <div className={styles.subheader}>
              {t('sidebar.job.performance')}
            </div>
            <div className={styles.divider} />
          </Row>

          <Row className={styles.row}>
            <Col span={12}>
              <div>
                {isNil(job?.filledOrders) ? FALLBACK_VALUE : job.filledOrders}
              </div>
              <div className={styles.label}>
                {t('sidebar.job.filledOrders')}
              </div>
            </Col>
            <Col span={12}>
              <div>
                {isNil(job?.offerSent) ? FALLBACK_VALUE : job.offerSent}
              </div>
              <div className={styles.label}>{t('sidebar.job.offersSent')}</div>
            </Col>
          </Row>

          <Row className={styles.row}>
            <Col span={12}>
              <div>
                {isNil(job?.interviewSent) ? FALLBACK_VALUE : job.interviewSent}
              </div>
              <div className={styles.label}>
                {t('sidebar.job.interviewSent')}
              </div>
            </Col>
            <Col span={12}>
              <div>
                {isNil(job?.numOfOrders) ? FALLBACK_VALUE : job.numOfOrders}
              </div>
              <div className={styles.label}>
                {t('sidebar.job.numberOfOrders')}
              </div>
            </Col>
          </Row>
        </>
      )}
    </aside>
  );
};

export default ClientJobView;
